import React, {useContext, useEffect, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Paper, Typography, useTheme} from "@mui/material";
import {CheckCircleOutlineRounded, CloseRounded, DeleteRounded, HighlightOffRounded, UploadRounded, VisibilityRounded} from "@mui/icons-material";
import {useFieldArray, useForm, FormProvider} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {yupResolver} from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import CheckStepper from "../check-stepper/CheckStepper";
import Preview from "../../preview/Preview";
import {ICheckEquipmentsForm, IEquipmentCommentForm, IEquipmentForm} from "../../models/models";
import {
	CHECK_EQUIPMENT_COMMENT_INITIAL_VALUE, CHECK_EQUIPMENT_INITIAL_VALUE, EQUIPMENT_COMMENT_FORM_VALIDATION_SCHEMA,
	EQUIPMENT_FORM_VALIDATION_SCHEMA, EQUIPMENTS_DEFAULT_EVENT_ID, MAX_NUM_EQUIPMENTS_COMMENTS } from "../config";
import {
	checkOutInStepState, checkEquipmentsState, raFetchedDataState,
	equipmentsEventState, checkOutInBufferDataActionTypeState, checkVehicleStatusState, checkOutInModeState
} from "../state";
import {FormCheckbox, FormTextField} from "../../common/form-fields-wrappers";
import {useMutation, useQuery} from "@tanstack/react-query";
import {vehicleEquipmentCheckOutIn} from "../../create-ra/services/http.services";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {fetchCheckOutInAssignEquipments, fetchCheckOutInCreateEquipments,
	fetchCheckOutInCreateForm, fetchCheckOutInUpdateVehicleEquipments
} from "../services/http.services";
import {deepClone} from "../../../../util/state/commonFunctions";
import CustomLoader from "../../../../util/component/CustomLoader";
import {getInsertionFlow} from "../services/utils";
import {resizeImg} from "../../utils";

const CheckEquipment = () => {
	const theme = useTheme();
	const {t} = useTranslation(['network', 'translation']);
	const { dispatchGlobalData } = useContext(GlobalContext);

	// const [updateState, setUpdateState] = useState<any>({});
	// TODO: testare se al back sono null
	const [equipCreatedFormId, setEquipCreatedFormId] = useState<any>();
	const [createEquipments, setCreateEquipments] = useState<any>();
	const [updateVehicleEquipments, setUpdateVehicleEquipments] = useState<any>();

	const [openPreviewAttach, setOpenPreviewAttach] = useState(false);
	const [filePreview, setFilePreview] = useState<any>(null);
	const [openDialogNewComment, setOpenDialogNewComment] = useState(false);

	const raFetchedData = useRecoilValue(raFetchedDataState);
	const checkVehicle = useRecoilValue(checkVehicleStatusState);
	const [checkStep, setCheckStep] = useRecoilState(checkOutInStepState);
	const setEquipmentsEvent = useSetRecoilState(equipmentsEventState);
	// form Values
	const [checkEquipments, setCheckEquipments] = useRecoilState<any>(checkEquipmentsState);
	// equipments.data - last check missing list
	const [bufferDataActionEquipments, setBufferDataActionEquipments] = useRecoilState(checkOutInBufferDataActionTypeState('equipments'));
	const checkOutInMode = useRecoilValue(checkOutInModeState);

	const equipmentsStatusForm = useForm<ICheckEquipmentsForm>({
			defaultValues: CHECK_EQUIPMENT_INITIAL_VALUE,
			resolver: yupResolver(
				EQUIPMENT_FORM_VALIDATION_SCHEMA
			)
		});

	const fieldArrayEquips = useFieldArray<ICheckEquipmentsForm>({
		control: equipmentsStatusForm.control,
		name: "equipments"
	});

	const fieldArrayComments = useFieldArray<ICheckEquipmentsForm>({
		control: equipmentsStatusForm.control,
		name: "comments"
	});

	const newCommentForm = useForm<IEquipmentCommentForm>({
		defaultValues: CHECK_EQUIPMENT_COMMENT_INITIAL_VALUE,
		resolver: yupResolver(
			EQUIPMENT_COMMENT_FORM_VALIDATION_SCHEMA
		)
	});

	const errors: any = equipmentsStatusForm.formState.errors;
	const equipmentsStatusValues: any = equipmentsStatusForm.getValues();

	const handleOpenDialogComment = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		setOpenDialogNewComment(true);
	}

	const handleCloseDialogComment = (data: any) => {
		setOpenDialogNewComment(false);
	};

	const handleContinue = (equipmentsStatus: ICheckEquipmentsForm) => {
		const bufferEquipments = deepClone(bufferDataActionEquipments);
		bufferEquipments.action = 'update';
		setBufferDataActionEquipments(bufferEquipments);
	}

	const handleDeleteAttachFile = (e: React.SyntheticEvent, index: number) => {
		e.stopPropagation();

		equipmentsStatusForm.setValue(`equipments.${index}.checkPhoto`, '');
		setFilePreview(null);

		equipmentsStatusForm.clearErrors();
	}

	const handleViewAttachFile = (e: React.SyntheticEvent, index: number) => {
		e.stopPropagation();

		setFilePreview({file: equipmentsStatusValues.equipments[index].base64Init + equipmentsStatusValues.equipments[index].checkPhoto});
		setOpenPreviewAttach(true);
	}

	const handleClosePreview = () => {
		setOpenPreviewAttach(false);
	}

	const handleFileOnChange = async (e: React.BaseSyntheticEvent, index: number) => {
		e.stopPropagation();

		if (!!e.target.files[0]?.size) {
			const file = e.target.files?.[0];
			const resizedFile = await resizeImg(e.target.files[0]) as File;
			let reader = new FileReader();
			// @ts-ignore
			reader.readAsDataURL(resizedFile);

			reader.onload = function() {
				const splitBase64 = !!reader.result ? (reader.result as string).split('base64,') : [];
				equipmentsStatusForm.setValue(`equipments.${index}.base64Init`, splitBase64[0] + 'base64,');
				equipmentsStatusForm.setValue(`equipments.${index}.checkPhoto`, splitBase64[1]);

				const fileExt2 = e.target.files[0].name.split('.');
				//console.log('fileExt2', fileExt2[fileExt2.length - 1]);
				const fileExt =
				resizedFile.type === "image/png" ? "png" :
				resizedFile.type === "image/jpeg" ? "jpg" :
				"bin";
				//console.log('fileExt', fileExt);
				equipmentsStatusForm.setValue(`equipments.${index}.fileExt`, fileExt);
				equipmentsStatusForm.clearErrors();
			};
		}
	}

	/** Add Comment */
	const handleAddComment = (comment: any) => {
		comment.createDatetime = (dayjs().toISOString());
		const comments = equipmentsStatusForm.getValues('comments');
		comments.push(comment);
		equipmentsStatusForm.setValue('comments', comments);

		newCommentForm.reset();
		setOpenDialogNewComment(false);
	}

	/** Delete Comment */
	const handleDeleteComment = (e: React.SyntheticEvent, field: string, index: number) => {
		e.stopPropagation();

		// remove driver accordion state
		const comments = equipmentsStatusForm.getValues('comments');
		comments.splice(index, 1);
		equipmentsStatusForm.setValue('comments', comments);
		equipmentsStatusForm.trigger();
	}

	/** Manage Checks*/
	const handleCheckEquipment = (e: React.SyntheticEvent, index: number, type: "present" | "missing", value: boolean) => {
		e.stopPropagation();

		if (type === "present") {
			equipmentsStatusForm.setValue(`equipments.${index}.missing`, false);
			equipmentsStatusForm.setValue(`equipments.${index}.checkPhoto`, '');
		}
		else if (type === "missing") {
			equipmentsStatusForm.setValue(`equipments.${index}.present`, false);
			equipmentsStatusForm.setValue(`equipments.${index}.checkPhoto`, '');
		}

		equipmentsStatusForm.clearErrors();
	}

	const isErrored = (index: number, field: string): boolean => {
		// @ts-ignore
		return !!errors?.equipments && !!errors.equipments[index] && !!errors.equipments[index][field];
	}

	const viewDefaultError = () => {
		dispatchGlobalData({
			type: 'SET_SNACKBAR',
			payload: {
				snackbar: {
					message: t('translation:error.tryLater'),
					type: snackbarTypes.error,
					show: true,
					autoClose: true,
				}
			}
		});
		setEquipCreatedFormId(null);
		setCreateEquipments(null);
	}

	const vehicleEquipmentDefaultQuery =
		useQuery(['vehicleEquipmentCheckOutIn', {tenant: raFetchedData.idTenant, plate: raFetchedData.vehicle.plate.code}],
		() => vehicleEquipmentCheckOutIn({tenant: raFetchedData.idTenant, plate:  raFetchedData.vehicle.plate.code}),
			{
		enabled: false,
		onSuccess: (response: any) => {
			if (response === "Error while getting vehicle equipment by tenant and plate") {
				viewDefaultError();
			}
			else {
				response.defaultEquipments
					.map((elem: any) => {
						fieldArrayEquips.append({
							data: elem,
							present: checkVehicle.nonRunningVehicle ? elem.associated : false,
							missing: checkVehicle.nonRunningVehicle ? !elem.associated : false,
							checkPhoto: '',
							fileExt: '',
							base64Init: ''
						});

						return null;
					});

				response.vehicleEquipmentCheckComments
					.map((elem: any) => fieldArrayComments.append(elem));
			}
		},
		onError: (error) => {
			viewDefaultError();
		}
	})

	/** Create Form */
	const createFormMutation = useMutation(
		fetchCheckOutInCreateForm,
		{
			onSuccess: (response: any) => {
				// response: botch :(
				if (typeof response === "string") { // error
					viewDefaultError();
				}
				else { // associate damages to created form
					if (!response?.[0]) {
						viewDefaultError();
					} else {
						setEquipCreatedFormId(response[0].id);
					}
				}
			},
			onError: (error) => {
				viewDefaultError();
			}
		});

	/** Update vehicle equipments */
	const updateVehicleEquipmentsMutation = useMutation(
		fetchCheckOutInUpdateVehicleEquipments,
		{
			onError: (error) => {
				viewDefaultError();
			}
		});

	/** Create Quipments */
	const createEquipmentsMutation = useMutation(
		fetchCheckOutInCreateEquipments,
		{
			onSuccess: (response: any) => {
				if (typeof response === "string" || !response) { // error
					viewDefaultError();
				}
				else { // associate damages to created form
					const missingEqList = response.map((meq: any) =>
						equipmentsStatusValues.equipments
							.filter((equip: any) => equip.data.code === meq.code)
							.map((equip: any) => {
								meq['associated'] = equip.data.associated;
								meq['priceInCents'] = equip.data.priceInCents;
								return meq;
							})
					).flat();
					setCreateEquipments(missingEqList); // assign prop new to every created
				}
			},
			onError: (error) => {
				viewDefaultError();
			}
		});

	/** Assign Equipments to event */
	const assignEquipmentsMutation = useMutation(
		fetchCheckOutInAssignEquipments,
		{
			onSuccess: (response: any) => {
				if (typeof response === "string" && response.includes("Error")) { // error
					viewDefaultError();
				}
				else { // associate damages to created form
					setUpdateVehicleEquipments(true);
				}
			},
			onError: (error) => {
				viewDefaultError();
			}
		});

	/**
	 *    Create the equipment form and save missing equipments
	 * @param missingEquip
	 * @param createEquipsList
	 */
	const saveMissingEquip = async (missingEquip: any, createEquipsList: any) => {
		await createFormMutation.mutateAsync({
			tenant: raFetchedData.idTenant,
			form: {
				"vin": raFetchedData.vehicle?.vin,
				"plate": raFetchedData.vehicle?.plate?.code,
				"raOwner": !!raFetchedData.raOwner?.firstName ? raFetchedData.raOwner.firstName + ' ' + raFetchedData.raOwner?.lastName : null,
				"raSectional": "Sectional",
				"raId": raFetchedData.idRentalAgreement,
				"formList": [{
					"counterparty": false,
					"missingEquipmentsForm": true,
					"eventId": EQUIPMENTS_DEFAULT_EVENT_ID
				}],
				insertionFlow: getInsertionFlow(checkOutInMode)
			}
		} as any);

		if (createEquipsList.length > 0) {
			const equipsList =
				equipmentsStatusValues.equipments
					.filter((equip: IEquipmentForm) => createEquipsList.includes(equip.data.code))
					.map((equip: IEquipmentForm) => ({
						code: equip.data.code,
						descr: equip.data.description,
						present: false,
						price: equip.data.priceInCents,
						plate: raFetchedData.vehicle.plate.code
					}));

			// create
			createEquipmentsMutation.mutate({
				tenant: raFetchedData.idTenant,
				equipsList: {equipmentList: equipsList}
			} as any);
		} else {
			setUpdateVehicleEquipments(true);
		}
	}

	/** Equip Action */
	useEffect(() => {
		if (!bufferDataActionEquipments?.action) {
			return;
		}

		// equipmentsStatusValues: new missing list -> valori della form
		const newMissingEquipCodeList =
			equipmentsStatusValues.equipments
				.filter((equipment: IEquipmentForm) => equipment.missing)
				.map((equipment: IEquipmentForm) => equipment.data.code);

		const lastCheckMissingList = // gia è missing
			!!bufferDataActionEquipments?.previousCheckData ?
				bufferDataActionEquipments.previousCheckData :
				[];
		const lastCheckMissingCodeList =
			lastCheckMissingList
				.map((equip: IEquipmentForm) => equip.data.code);

		// // to delete equip list
		// const delEquipsList =
		// 	lastCheckMissingList
		// 		.filter((equip: IEquipmentForm) => !newMissingEquipCodeList.includes(equip.data.code))
		// 		.map((equip: IEquipmentForm) => equip.data.code);

		// to create equip list
		const createEquipsList =
			newMissingEquipCodeList
				.filter((code: string) => !lastCheckMissingCodeList.includes(code));

		if (!raFetchedData.bypassDamagesEquipments) {
			const missingEquip =
				equipmentsStatusValues.equipments
					.filter((equipment: IEquipmentForm) => equipment.data.associated && equipment.missing);

			if (missingEquip.length > 0) {
				saveMissingEquip(missingEquip, createEquipsList);
			} else {
				setUpdateVehicleEquipments(true);
			}
		} else {
			setUpdateVehicleEquipments(true);
		}
	},[bufferDataActionEquipments]);

	/** assign equips to form */
	useEffect(() => {
		if (!!equipCreatedFormId && !!createEquipments && createEquipments.length > 0) {
			assignEquipmentsMutation.mutate({
				tenant:  raFetchedData.idTenant,
				payload: [{
					formId: equipCreatedFormId,
					equipmentIdList: createEquipments.map((elem: any) => elem.id)
				}]} as any);
		} else if (!!createEquipments && createEquipments.length === 0) {
			setUpdateVehicleEquipments(true);
		}
	}, [equipCreatedFormId, createEquipments]);

	/** updateVehicleEquipments */
	useEffect(() => {
		if (!!updateVehicleEquipments) {
			// operation
			updateVehicleEquipmentsMutation.mutate({
				tenant:  raFetchedData.idTenant,
				plate: raFetchedData.vehicle.plate.code,
				body: {
					equipmentVehicleList:
						equipmentsStatusValues.equipments
							.map((elem: any) => {
								// immagini solo per equip mancanti
								return {
									equipmentId: elem.data.id,
									associated: elem.present,
									imageName: elem.missing ?
										raFetchedData.vehicle.plate.code + '_' + elem.data.code + '.' + elem.fileExt :
										null,
									imageNote: null,
									imageContentBase64:  elem.missing ? elem.checkPhoto : null
								}
							}),
					vehicleEquipmentCheckComments:
						equipmentsStatusValues.comments
							.map((elem: any) => {
								return {
									"id": !!elem.id ? elem.id : null,
									"note": elem.note
								}
							})
				}
			} as any);

			setBufferDataActionEquipments({
				action: null,
				previousCheckData: deepClone(equipmentsStatusValues.equipments.filter((equipment: IEquipmentForm) => equipment.missing))
			});

			setEquipmentsEvent({
				id: equipCreatedFormId, 		// formId
				equipments: createEquipments 	// created equipments
			});

			setCheckEquipments(equipmentsStatusValues);

			setCheckStep({
				active: checkStep.active + 1,
				numSteps: checkStep.numSteps
			});
		}
	}, [updateVehicleEquipments])

	useEffect(() => {
		if (!!checkEquipments) {
			fieldArrayEquips.remove();
			checkEquipments.equipments
				.forEach((elem: any) =>	{
					fieldArrayEquips.append(elem);
				});

			fieldArrayComments.remove();
			checkEquipments.comments
				.map((elem: any) => fieldArrayComments.append(elem));
		} else {
			vehicleEquipmentDefaultQuery.refetch();
		}
	},[]);

	// @ts-ignore
	return (
		<Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>

			<FormProvider {...equipmentsStatusForm}>
				<Box>
					<Grid container columnSpacing={2}>
						<Grid item xs={12} sx={{display: "flex", justifyContent: "center"}}>
							<Typography variant={"h6"}>
								{t("raCheckOutIn.checkEquipments.header.title")}
							</Typography>
						</Grid>
						<Grid item xs={12} sx={{display: "flex", justifyContent: "center", mb: theme.spacing(2)}}>
							<Typography variant={"bodyLarge"}>
								{t("raCheckOutIn.checkEquipments.header.subtitle")}
							</Typography>
						</Grid>

						{/* Content */}
						<Grid item xs={6} sx={{maxWidth: "540px !important", ml: theme.spacing(2)}}/>
						<Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
							<Typography variant={"bodyLarge"}>
								{t("raCheckOutIn.checkEquipments.equips.labelPresent")}
							</Typography>
						</Grid>
						<Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
							<Typography variant={"bodyLarge"}>
								{t("raCheckOutIn.checkEquipments.equips.labelAbsent")}
							</Typography>
						</Grid>

						{
							// raVehicleData.equipments
							equipmentsStatusValues.equipments.length > 0 &&
							equipmentsStatusValues.equipments
								.map((elem: any, index: number) =>
									<Grid container item key={index}
										  sx={{bgcolor: theme.palette.neutral.main, borderRadius: 2, py: theme.spacing(1), my: theme.spacing(1), ml: theme.spacing(2)}}>
							 			<Grid item xs={6} sx={{py: "auto", display: "flex", alignItems: "center", maxWidth: "540px !important"}}>
							 				<Box>
												<Typography variant={"body"} component={"div"}>
													{t(`preview.equipment.${elem.data.code}`)}
												</Typography>

												<Typography variant={"bodySmall"} component={"div"} sx={{display: "flex", alignItems: "center"}}>
													<Typography variant={"bodySmall"} component={"div"} sx={{display: "flex", alignItems: "center"}}>
														{elem.data.associated ?
															<CheckCircleOutlineRounded sx={{
																color: theme.palette.success.main ,
																fontSize: 'small',
																mr: theme.spacing(0.5)
															}}/> :
															<HighlightOffRounded sx={{
																color: theme.palette.error.main ,
																fontSize: 'small',
																mr: theme.spacing(0.5)
															}}/>}
														{dayjs(elem.data.createDatetime).format('DD/MM/YYYY HH:mm')}
													</Typography>
												</Typography>

											</Box>
							 			</Grid>

							 			<Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
											<FormCheckbox<ICheckEquipmentsForm>
												name={`equipments.${index}.present`}
												label={''}
												color={'primary'}
												sx={{color: (errors?.equipments?.[index]?.present !== undefined) ? theme.palette.error.main : theme.palette.primary.main}}
												onChange={(e: React.SyntheticEvent, value: boolean) => {handleCheckEquipment(e, index, 'present', value)}}
												disabled={checkVehicle.nonRunningVehicle}
											/>
							 			</Grid>
							 			<Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
											<FormCheckbox<ICheckEquipmentsForm>
												name={`equipments.${index}.missing`}
												label={''}
												color={'primary'}
												sx={{color: (errors?.equipments?.[index]?.missing !== undefined) ? theme.palette.error.main : theme.palette.primary.main}}
												onChange={(e: React.SyntheticEvent, value: boolean) => {handleCheckEquipment(e, index, 'missing', value)}}
												disabled={checkVehicle.nonRunningVehicle}
											/>
							 			</Grid>
										<Grid item xs={2} sx={{display: "flex", justifyContent: "center", alignItems: "centers"}}>
											{
												// @ts-ignore
												!!equipmentsStatusValues.equipments[index].missing && !equipmentsStatusValues.equipments[index].checkPhoto &&
												<Box  sx={{display: "flex", justifyContent: "center"}}>
												<IconButton color={isErrored(index, 'checkPhoto') ? "error" : "primary"}
															component="label">
													<input hidden accept="image/*;capture=camera"
														   type="file"
														   onChange={(e: React.SyntheticEvent) => handleFileOnChange(e, index)}
														/>
														<UploadRounded />
													</IconButton>
												</Box>
											}

											{
												equipmentsStatusValues.equipments[index].missing && !!equipmentsStatusValues.equipments[index].checkPhoto &&
												<Box  sx={{display: "flex", justifyContent: "center"}}>
													<IconButton color="primary" onClick={(e: React.SyntheticEvent) => handleViewAttachFile(e, index)}>
														<VisibilityRounded />
													</IconButton>
													<IconButton color="primary" onClick={(e: React.SyntheticEvent) => handleDeleteAttachFile(e, index)}>
														<DeleteRounded />
													</IconButton>
												</Box>
											}
										</Grid>
									</Grid>
								)
						}

						{
							 equipmentsStatusValues.comments.length < MAX_NUM_EQUIPMENTS_COMMENTS &&
							 <Grid container item xs={12} justifyContent="flex-end">
							 	<Box>
							 		<Button variant={'text'} color={"secondary"}  onClick={handleOpenDialogComment}>
						 				{t('raCheckOutIn.checkEquipments.button.addComments')}
							 		</Button>
							 	</Box>
							 </Grid>
						}
					</Grid>

					{
						equipmentsStatusValues.comments.length > 0 &&
						<Grid container columnSpacing={2}>
							<Grid item xs={12} sx={{ml: theme.spacing(1)}}>
								<Typography variant={"bodyLarge"}>
									{t("raCheckOutIn.checkEquipments.equips.labelComments")}
								</Typography>
							</Grid>
							{
								equipmentsStatusValues.comments
									.map((elem: any, index: number) =>
										<Grid container item xs={12} key={index}
											  sx={{bgcolor: theme.palette.neutral.main, borderRadius: 2, py: theme.spacing(1), my: theme.spacing(1), ml: theme.spacing(2)}}>
											<Grid item xs={2} sx={{display: "flex", alignItems: "center"}}>
												<Typography variant={"body"}>
													{dayjs(elem.createDatetime).format('DD/MM/YYYY HH:mm')}
												</Typography>
											</Grid>
											<Grid item xs={9} sx={{display: "flex", alignItems: "center"}}>
												<Typography variant={"body"}>
													{elem.note}
												</Typography>
											</Grid>
											<Grid item xs={1} sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(1)}}>
												<IconButton color="primary" onClick={(e: React.SyntheticEvent) => handleDeleteComment(e, `equipmentComments[${index}]`, index)}>
													<DeleteRounded/>
												</IconButton>
											</Grid>
										</Grid>
									)
							}
						</Grid>
					}

				</Box>

			</FormProvider>

			<CheckStepper />

			<Box sx={{display: "flex", justifyContent: "center", mt: theme.spacing(2)}}>
				<Button color={"accent"}
						onClick={equipmentsStatusForm.handleSubmit(handleContinue)}>
					{t('raCheckOutIn.checkEquipments.button.continue')}
				</Button>
			</Box>

			<Dialog fullWidth={true} maxWidth={'md'} onClose={handleCloseDialogComment}	open={openDialogNewComment}>
				<DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
						<span>
							{t('raCheckOutIn.checkEquipments.button.addComments')}
						</span>

					<IconButton onClick={handleCloseDialogComment} sx={{ml: theme.spacing(4), mr: "0px"}}>
						<CloseRounded />
					</IconButton>
				</DialogTitle>

				<DialogContent>
					<FormProvider {...newCommentForm}>
						<Box>
							<FormTextField<IEquipmentCommentForm>
								name={`note`}
								label={t('raCheckOutIn.checkEquipments.form.label.note')}
								control={newCommentForm.control}
							/>
						</Box>
					</FormProvider>
				</DialogContent>

				<DialogActions>
					<Button color={"accent"} onClick={newCommentForm.handleSubmit(handleAddComment)}>
						{t('raCheckOutIn.checkEquipments.button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>

			<Preview
				open={openPreviewAttach}
				attach={filePreview}
				onClose={handleClosePreview}
			/>

			<CustomLoader loading={vehicleEquipmentDefaultQuery.isFetching || createFormMutation.isLoading || createEquipmentsMutation.isLoading || assignEquipmentsMutation.isLoading}/>
		</Paper>
	);
}

export default CheckEquipment;
